import type { ReactNode } from 'react';
import { TopBar } from './TopBar.js';
import { Sidebar } from './Sidebar.js';
import { AppFooter } from './AppFooter.js';
import { TopBarProvider } from '../contexts/TopBarContext.js';
import { AssistantProvider } from '../contexts/AssistantContext.js';
import { HelpPanel } from './ui/HelpPanel.js';
import { useAyudaPanel } from '../hooks/useAyudaPanel.js';
import { rutaAModulo } from '../lib/rutaAModulo.js';
import { modulosConRecorrido } from '@evo-hub/help-content/recorridos';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssistantDock } from './assistant/AssistantDock.js';
import { VoiceConversationOverlay } from './assistant/VoiceConversationOverlay.js';
import { TrialNotice } from './subscription/TrialNotice.js';
import { NavSeenProvider } from '../contexts/NavSeenContext.js';
import { AvisoSonoro } from './assistant/AvisoSonoro.js';
import { useUiFlag } from '../hooks/useUiPrefs.js';

interface AppShellProps {
  children: ReactNode;
}

// Evo Brain (2026-08-05): AssistantDock vive ACÁ, hermano de Sidebar/<main>,
// no adentro de cada página — es el punto exacto por el que sobrevive a
// cualquier cambio de ruta (las páginas solo se registran, ver
// contexts/AssistantContext.tsx). AssistantProvider tiene que envolver tanto
// <main>{children}</main> (las páginas, que se registran) como AssistantDock
// (que lee lo registrado) — mismo nivel que TopBarProvider.
/** ¿Este módulo tiene recorrido escrito? Sale de los recorridos mismos, no de
 *  una lista aparte que habría que mantener en paralelo. */
function tieneRecorrido(modulo: string): boolean {
  return (modulosConRecorrido('es') as string[]).includes(modulo);
}

export function AppShell({ children }: AppShellProps) {
  // El estado vive en el servidor, por usuario: cerrarlo acá lo deja cerrado
  // mañana y en otra máquina. Ver hooks/useUiPrefs.ts.
  const ayuda = useAyudaPanel();
  const location = useLocation();
  const navigate = useNavigate();
  const moduloActual = rutaAModulo(location.pathname);
  const [collapsed, setCollapsed] = useUiFlag('sidebar.collapsed');


  return (
    <TopBarProvider>
      <AssistantProvider>
        {/* Campana y voz de lo que entró. No pinta nada; va acá porque necesita
            estar dentro de AssistantProvider para saber si el modo de voz está
            abierto. */}
        <AvisoSonoro />
        {/* Marca visto el módulo de la ruta y expone qué era nuevo en esta
            visita. Va acá y no en cada página: entrar por URL directa o con el
            botón atrás no pasa por el clic del menú, y ocho copias del mismo
            efecto divergen en el primer cambio. */}
        <NavSeenProvider>
        {/* Fondo V2: --evo-bg-gradient se declara UNA sola vez, acá en el raíz.
            Nada adentro (tarjetas, listas, paneles) vuelve a declarar fondo. */}
        <div
          className="flex flex-col h-full overflow-hidden"
          style={{ backgroundColor: 'var(--evo-bg)', backgroundImage: 'var(--evo-bg-gradient)' }}
        >
          <TopBar collapsed={collapsed} />
          {/* Días restantes de prueba, a la vista en toda la app y no solo en
              Ajustes: alguien que está probando la herramienta no entra a
              Ajustes, y enterarse de que se venció el día que deja de poder
              escribir es la peor forma de enterarse. Se esconde solo cuando no
              hay nada que decir (ver TrialNotice). */}
          <TrialNotice />
          <div className="flex flex-1 overflow-hidden">
            <Sidebar collapsed={collapsed} />
            <main className="flex-1 overflow-hidden">{children}</main>
            {/* Los dos paneles del borde derecho. Nunca están abiertos a la
                vez: useAyudaPanel colapsa el asistente al abrir la ayuda
                (Help Panel Pattern, decisión de Eduardo 2026-09-12). */}
            <AssistantDock />
            <HelpPanel
              abierto={ayuda.abierta}
              onCerrar={ayuda.cerrar}
              moduloActual={moduloActual}
              // «Ver el recorrido otra vez» reusa el mecanismo que ya existe y
              // ya se probó en producción: `?recorrido=1` en la ruta actual, que
              // lo fuerza aunque esta persona ya lo haya visto. Solo se ofrece
              // si este módulo TIENE recorrido — un botón que no hace nada es
              // peor que no tenerlo.
              {...(moduloActual !== null && tieneRecorrido(moduloActual)
                ? { onVerRecorrido: () => { navigate(`${location.pathname}?recorrido=1`); } }
                : {})}
            />
          </div>
          <AppFooter collapsed={collapsed} onToggle={() => { setCollapsed(!collapsed); }} />
          <VoiceConversationOverlay />
        </div>
        </NavSeenProvider>
      </AssistantProvider>
    </TopBarProvider>
  );
}
